/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  exhaustMap,
  filter,
  Observable,
  switchMap,
  take,
  throwError
} from 'rxjs';
import { AuthFacade } from '~backoffice/app/~store/facades/auth.facade';
import { SKIP_JWT_INTERCEPTOR } from '../constants/global.constants';
import { AuthenticationService } from '../services/authentication.service';
import { extractFromRequestData } from '@veone/ngx-utilities/form';

export function jwtInterceptor(
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const authFacade = inject(AuthFacade);
  const authService = inject(AuthenticationService);
  const { value: skipJwtInterceptor, request: reqWithoutSkipJwtInterceptor } =
    extractFromRequestData<boolean>(request, 'skipJwtInterceptor');
  const { value: skipJwtInterceptorErrors, request: finalRequest } =
    extractFromRequestData<boolean>(
      reqWithoutSkipJwtInterceptor,
      'skipJwtInterceptorErrors'
    );

  if (
    finalRequest.headers.get('Authorization') ||
    finalRequest.context.get(SKIP_JWT_INTERCEPTOR) === true ||
    skipJwtInterceptor
  ) {
    return next(finalRequest);
  }

  return authFacade.accessToken$.pipe(
    take(1),
    switchMap(accessToken => {
      if (accessToken) {
        authService.lastUsedToken = accessToken.id;
        return next(
          finalRequest.clone({
            setHeaders: { authorization: `Bearer ${accessToken.id}` }
          })
        );
      }
      return next(finalRequest);
    }),
    catchError(error => {
      const wwwAuthenticate = error.headers.get('WWW-Authenticate') as
        | string
        | null;
      if (
        (!!wwwAuthenticate &&
          !wwwAuthenticate.toLocaleLowerCase().includes('bearer')) ||
        skipJwtInterceptorErrors
      ) {
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse && 401 === error.status) {
        return authFacade.refreshToken$.pipe(
          take(1),
          filter(Boolean),
          switchMap(() => authFacade.refreshAccessToken()),
          exhaustMap(() => authFacade.accessToken$.pipe(take(1))),
          switchMap(accessToken => {
            if (!!accessToken && accessToken.id !== authService.lastUsedToken) {
              return next(
                finalRequest.clone({
                  setHeaders: { authorization: `Bearer ${accessToken.id}` }
                })
              );
            } else {
              return throwError(() => error);
            }
          })
        );
      }

      return throwError(() => error);
    })
  );
}
